// ============================================================================
// ORDERS · Desk 2.0 — Models: which picture a model shows, and how the read is cut (OD2-S2b)
// ============================================================================
// Pure. The Sales master holds several rows per model (one per colour). The Models
// screen shows ONE picture per model, chosen the same way every time: a row with a
// barcode beats a row without, then the lowest barcode string, then the lowest
// image address. Only `image_small` is ever used (never a transform, never the
// large file). A model with no usable row has no entry - the screen says so.
// The read is cut into chunks of codes; PostgREST returns at most 1,000 rows per
// request, so a chunk that comes back with exactly that many is treated as cut
// off and split again - never silently dropped.
// ============================================================================

import { chunkIds, PG_IN_MAX, PG_PAGE } from './pgIn';

export const PHOTO_CHUNK = 40;   // codes per request: about 25 rows each in the Sales master, far below the row cap

const bc = (r) => (r && r.barcode != null ? String(r.barcode).trim() : '');
const url = (r) => (r && r.image_small != null ? String(r.image_small).trim() : '');

/** The deterministic order of two rows of one model: with barcode first, then lowest barcode, then lowest address. */
export function betterRow(a, b) {
  const ba = bc(a); const bb = bc(b);
  if (!!ba !== !!bb) return ba ? -1 : 1;
  if (ba !== bb) return ba < bb ? -1 : 1;
  const ua = url(a); const ub = url(b);
  return ua < ub ? -1 : ua > ub ? 1 : 0;
}

/** Rows -> Map(model -> { src, barcode|null }). Rows without a model or without an image address are ignored. */
export function pickPhotos(rows) {
  const best = new Map();
  (rows || []).forEach((r) => {
    if (!r || !r.model || !url(r)) return;
    const cur = best.get(r.model);
    if (!cur || betterRow(r, cur) < 0) best.set(r.model, r);
  });
  const out = new Map();
  best.forEach((r, model) => out.set(model, { src: url(r), barcode: bc(r) || null }));
  return out;
}

/** The codes the read asks for: distinct, non-empty, in a stable order. */
export const photoCodes = (itemNos) => [...new Set((itemNos || []).filter(Boolean))].sort();

/** Chunks of codes, never larger than the guard allows. */
export const photoChunks = (codes, size = PHOTO_CHUNK) => chunkIds(codes, Math.min(size, PG_IN_MAX));

/**
 * Read every chunk through `fetchRows(codes) -> rows`. A chunk that returns the row cap is cut off: it is halved and both halves
 * read again, down to a single code (one model with 1,000 rows is more than the screen needs, and is reported in `cut`).
 * Resolves { rows, cut: [codes that stayed cut off], requests }.
 */
export async function readPhotoRows(codes, fetchRows, { size = PHOTO_CHUNK, cap = PG_PAGE } = {}) {
  const rows = []; const cut = []; let requests = 0;
  const run = async (list) => {
    requests += 1;
    const got = (await fetchRows(list)) || [];
    if (got.length >= cap && list.length > 1) {
      const mid = Math.ceil(list.length / 2);
      await run(list.slice(0, mid)); await run(list.slice(mid));
      return;
    }
    if (got.length >= cap) cut.push(list[0]);
    rows.push(...got);
  };
  for (const c of photoChunks(codes, size)) await run(c);
  return { rows, cut, requests };
}
